import { PageSearchInput } from "@client/components/PageSearchInput";
import type {
  SortOption,
  TrackCategory,
} from "@client/hooks/use-my-evaluations-data";
import {
  ALL_STATUSES,
  ALL_TRACK_CATEGORIES,
} from "@client/hooks/use-my-evaluations-data";
import { cn } from "@client/utils/cn";
import isTextEditingElement from "@client/utils/isTextEditingElement";
import type { EvaluationState } from "@engine/round/aggregate";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@sfc/ui";
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  ListChecks,
  SearchIcon,
  TrainTrack,
  X,
} from "lucide-react";
import {
  forwardRef,
  useEffect,
  useRef,
  type ComponentType,
  type ReactNode,
} from "react";

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const TRACK_LABELS: Record<TrackCategory, string> = {
  both: "Freedom & Fairness",
  freedom: "Freedom only",
  fairness: "Fairness only",
  none: "No Track",
};

const STATUS_LABELS: Record<EvaluationState, string> = {
  "not-started": "Not Started",
  evaluated: "Evaluation Started",
  deferred: "Deferred",
  recused: "Recused",
  canceled: "Canceled",
};

const SORT_LABELS: Record<SortOption, string> = {
  "search-relevance": "Relevance",
  "last-touched": "Last Touched",
  "base-request": "Base Request",
  "org-name": "Proposal Name",
  status: "Status",
  "investigation-interest": "Investigation Interest",
  "review-progress": "Review Progress",
  notes: "Notes",
};

// ---------------------------------------------------------------------------
// Helpers — trigger labels summarize the current selection
// ---------------------------------------------------------------------------

/** Format a multi-select filter's trigger label.
 *
 * - No selection (or full selection) → "Any"  (filter inactive)
 * - 1 selected  → that value
 * - 2+ selected → "N selected"  (kept short so truncate engages)
 */
function multiSelectLabel<T extends string>(
  selected: T[],
  all: readonly T[],
  labelFor: (value: T) => string,
): string {
  if (selected.length === 0 || selected.length === all.length) return "Any";
  if (selected.length === 1) return labelFor(selected[0]!);
  return `${selected.length} selected`;
}

// ---------------------------------------------------------------------------
// FilterTrigger — unified trigger chip used by every dropdown filter
//
// Three-tier responsive shrink keeps the whole filter bar on one row until
// true mobile:
//   xl  (≥1280): icon + "Label" + value + chevron  (widest)
//   lg  (≥1024): icon + value + chevron            (no "Label" prefix)
//   <lg        : icon only (w-9, everything else hidden) — paired with a
//                bottom tooltip at the call site so the filter is still
//                discoverable in icon-only mode.
// ---------------------------------------------------------------------------

type FilterTriggerProps = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  active: boolean;
  widthClass: string;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">;

const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  function FilterTrigger(
    { icon: Icon, label, value, active, widthClass, className, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        data-active={active ? "true" : undefined}
        {...props}
        className={cn(
          "group flex h-9 items-center justify-center lg:justify-start gap-2 rounded-md border px-0 lg:px-3 text-sm transition-colors cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          active
            ? "border-accent/60 bg-accent/10 text-foreground hover:bg-accent/15"
            : "border-border-subtle bg-surface-elevated text-text-secondary hover:bg-surface-hover",
          widthClass,
          className,
        )}
      >
        <Icon
          className={cn(
            "size-4 shrink-0",
            active ? "text-accent" : "text-muted-foreground",
          )}
        />
        <span className="hidden lg:flex min-w-0 flex-1 items-baseline gap-1.5 truncate">
          <span className="hidden xl:inline text-muted-foreground">
            {label}
          </span>
          <span className={cn("truncate", active && "font-medium")}>
            {value}
          </span>
        </span>
        <ChevronDown className="hidden lg:inline-block size-4 shrink-0 opacity-60" />
      </button>
    );
  },
);

// ---------------------------------------------------------------------------
// MultiSelectHeader — animated "N selected" + Clear row
//
// Appears only when something is selected: the grid-rows transition collapses
// to height 0 when empty, so there's no disabled/empty row taking up space in
// the dropdown's idle state.
// ---------------------------------------------------------------------------

function MultiSelectHeader({
  selectedCount,
  onClear,
}: {
  selectedCount: number;
  onClear: () => void;
}) {
  const open = selectedCount > 0;
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-200 ease-out",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div className="overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-2 pt-1.5 pb-1.5">
          <span className="text-xs text-muted-foreground">
            {selectedCount} selected
          </span>
          <button
            type="button"
            className="text-xs font-medium text-accent hover:underline"
            onClick={onClear}
          >
            Clear
          </button>
        </div>
        <DropdownMenuSeparator className="mb-1" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProposalsFilters
// ---------------------------------------------------------------------------

export function ProposalsFilters({
  isRecommender,
  isObserver = false,
  sort,
  onSortChange,
  trackFilter,
  onTrackFilterChange,
  statusFilter,
  onStatusFilterChange,
  showHidden,
  onShowHiddenChange,
  searchQuery,
  searchActive,
  onSearchChange,
  hasActiveFilters,
  onClearFilters,
}: {
  isRecommender: boolean;
  isObserver?: boolean;
  sort: SortOption;
  onSortChange: (sort: SortOption) => void;
  trackFilter: TrackCategory[];
  onTrackFilterChange: (filter: TrackCategory[]) => void;
  statusFilter: EvaluationState[];
  onStatusFilterChange: (filter: EvaluationState[]) => void;
  showHidden: boolean;
  onShowHiddenChange: (show: boolean) => void;
  searchQuery: string;
  searchActive: boolean;
  onSearchChange: (query: string) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  // A filter's trigger is styled "active" only when it's actually narrowing
  // results — i.e., a strict subset is selected. Full selection is equivalent
  // to empty (no narrowing) so we render it the same.
  const trackActive =
    trackFilter.length > 0 && trackFilter.length < ALL_TRACK_CATEGORIES.length;
  const statusActive =
    statusFilter.length > 0 && statusFilter.length < ALL_STATUSES.length;

  const trackLabel = multiSelectLabel(
    trackFilter,
    ALL_TRACK_CATEGORIES,
    (c) => TRACK_LABELS[c],
  );
  const statusLabel = multiSelectLabel(
    statusFilter,
    ALL_STATUSES,
    (s) => STATUS_LABELS[s],
  );
  const sortLabel = SORT_LABELS[sort];
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.key !== "/" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }

      const active = document.activeElement;
      if (isTextEditingElement(active) || isTextEditingElement(event.target)) {
        return;
      }

      event.preventDefault();
      searchInputRef.current?.focus();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const sortOptions: { field: SortOption; label: string }[] = [
    ...(searchActive
      ? [{ field: "search-relevance" as const, label: "Relevance" }]
      : []),
    ...(isRecommender
      ? [
          { field: "last-touched" as const, label: "Last Touched" },
          { field: "base-request" as const, label: "Base Request" },
          { field: "org-name" as const, label: "Proposal Name" },
          { field: "status" as const, label: "Status" },
          {
            field: "investigation-interest" as const,
            label: "Investigation Interest",
          },
          { field: "review-progress" as const, label: "Review Progress" },
        ]
      : isObserver
        ? [
            { field: "notes" as const, label: "Notes" },
            { field: "base-request" as const, label: "Base Request" },
            { field: "org-name" as const, label: "Proposal Name" },
          ]
        : [
            { field: "base-request" as const, label: "Base Request" },
            { field: "org-name" as const, label: "Proposal Name" },
          ]),
  ];

  return (
    <TooltipProvider delay={300}>
      <div className="flex flex-wrap items-center gap-2">
        {/* Sort (not a filter — doesn't narrow results) */}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger
              render={
                <DropdownMenuTrigger
                  render={
                    <FilterTrigger
                      icon={ArrowUpDown}
                      label="Sort"
                      value={sortLabel}
                      active={false}
                      aria-label={`Sort: ${sortLabel}`}
                      widthClass="w-9 lg:w-[160px] xl:w-[180px]"
                    />
                  }
                />
              }
            />
            <TooltipContent side="bottom" className="lg:hidden">
              Sort: {sortLabel}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="start" className="min-w-44">
            {sortOptions.map(({ field, label }) => (
              <DropdownMenuItem
                key={field}
                onClick={() => onSortChange(field)}
                className="relative pl-8"
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {sort === field && <Check className="h-4 w-4" />}
                </span>
                <span className={cn(sort === field && "font-medium")}>
                  {label}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Track filter — single-select (Any / Freedom & Fairness / Freedom only / Fairness only / No Track) */}
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger
              render={
                <DropdownMenuTrigger
                  render={
                    <FilterTrigger
                      icon={TrainTrack}
                      label="Track"
                      value={trackLabel}
                      active={trackActive}
                      aria-label={`Track: ${trackLabel}`}
                      widthClass="w-9 lg:w-[170px] xl:w-[200px]"
                    />
                  }
                />
              }
            />
            <TooltipContent side="bottom" className="lg:hidden">
              Track: {trackLabel}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="start" className="min-w-56">
            {(
              [
                { value: null, label: "Any" },
                ...ALL_TRACK_CATEGORIES.map((cat) => ({
                  value: cat,
                  label: TRACK_LABELS[cat],
                })),
              ] as const
            ).map(({ value, label }) => {
              // "Any" matches whenever the trigger reads "Any" — both the
              // empty-set and full-set cases `multiSelectLabel` collapses.
              // Non-"Any" rows check membership so a multi-value URL like
              // `?track=freedom,fairness` still shows its selected rows
              // instead of leaving the dropdown with no checkmark.
              const active =
                value === null ? !trackActive : trackFilter.includes(value);
              return (
                <DropdownMenuItem
                  key={value ?? "any"}
                  onClick={() =>
                    onTrackFilterChange(value === null ? [] : [value])
                  }
                  className="relative pl-8"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {active && <Check className="h-4 w-4" />}
                  </span>
                  <span className={cn(active && "font-medium")}>{label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Status filter — multi-select (recommenders only) */}
        {isRecommender && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger
                render={
                  <DropdownMenuTrigger
                    render={
                      <FilterTrigger
                        icon={ListChecks}
                        label="Status"
                        value={statusLabel}
                        active={statusActive}
                        aria-label={`Status: ${statusLabel}`}
                        widthClass="w-9 lg:w-[160px] xl:w-[180px]"
                      />
                    }
                  />
                }
              />
              <TooltipContent side="bottom" className="lg:hidden">
                Status: {statusLabel}
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="min-w-56">
              <MultiSelectHeader
                selectedCount={statusFilter.length}
                onClear={() => onStatusFilterChange([])}
              />
              {ALL_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={statusFilter.includes(status)}
                  onCheckedChange={(checked) => {
                    onStatusFilterChange(
                      checked
                        ? [...statusFilter, status]
                        : statusFilter.filter((s) => s !== status),
                    );
                  }}
                >
                  {STATUS_LABELS[status]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Visibility (recommenders only) */}
        {isRecommender && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger
                render={
                  <DropdownMenuTrigger
                    render={
                      <FilterTrigger
                        icon={showHidden ? EyeOff : Eye}
                        label="Show"
                        value={showHidden ? "Hidden" : "Visible"}
                        active={showHidden}
                        aria-label={`Show: ${showHidden ? "Hidden" : "Visible"}`}
                        widthClass="w-9 lg:w-[140px] xl:w-[150px]"
                      />
                    }
                  />
                }
              />
              <TooltipContent side="bottom" className="lg:hidden">
                Show: {showHidden ? "Hidden" : "Visible"}
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="min-w-48">
              {(
                [
                  { value: false, label: "Visible proposals", Icon: Eye },
                  { value: true, label: "Hidden only", Icon: EyeOff },
                ] as const
              ).map(({ value, label, Icon }) => {
                const active = showHidden === value;
                return (
                  <DropdownMenuItem
                    key={label}
                    onClick={() => onShowHiddenChange(value)}
                    className="relative pl-8"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {active && <Check className="h-4 w-4" />}
                    </span>
                    <Icon className="size-4 text-muted-foreground" />
                    <span className={cn(active && "font-medium")}>{label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Search — flexes and shrinks inline with the filters. Only wraps to
            its own row at true phone widths (<400px). */}
        <div className="relative flex-1 min-w-[80px] lg:min-w-[180px] xl:min-w-[200px] max-[400px]:basis-full">
          <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <PageSearchInput
            ref={searchInputRef}
            type="text"
            role="searchbox"
            placeholder="Search proposals..."
            aria-keyshortcuts="/ Meta+K Control+K"
            className="h-9 border-border-subtle bg-surface-elevated pl-9 pr-9 text-foreground"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {/* Clear filters — only visible when anything is narrowing. Label
            hides at <xl so the button doesn't wrap the row; tooltip covers
            that state. `border-0` overrides the ghost variant's outline to
            match the designer's flat treatment. */}
        {hasActiveFilters && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Clear filters"
                  className="h-9 shrink-0 gap-1.5 border-0 shadow-none text-text-secondary hover:bg-surface-hover hover:text-foreground"
                  onClick={onClearFilters}
                />
              }
            >
              <X className="size-4" />
              <span className="hidden xl:inline">Clear filters</span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="xl:hidden">
              Clear filters
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
